import { FunctionComponent, KeyboardEvent, MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Check, ChevronDown, ExternalLink, Lock, Search, X } from 'lucide-react';
import { styles } from '../../../../styles/themes/style';
import SkeletonBone from '../../../../components/SkeletonBone';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { ISuppressionList } from '../../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { describeListScope, listAppliesTo } from '../../../../utils/suppressionListUtils';

/**
 * Campaign settings → Suppression lists.
 *
 * Lists that bind every campaign (and other members' reserved lists) apply here whether
 * or not anyone picks them, so they are shown as locked chips, not offered. Opt-in lists
 * ('selected') are picked in a dropdown, which stays short however many lists the
 * workspace makes. Picking one takes matching leads out of this campaign on save.
 */

interface ICampaignSuppressionListsProps {
  /** The campaign's owner: their own reserved list does not bind their campaigns. */
  createdBy?: string;
  value: string[];
  onChange: (ids: string[]) => void;
}

/** Always-on lists shown before "+N more". */
const ALWAYS_PREVIEW = 4;
/** From this many opt-in lists the dropdown gets a search box. */
const SEARCH_FROM = 6;
/** Below this much room under the field, the dropdown opens upward. */
const MENU_ROOM = 340;

function entries(n: number): string {
  return `${n.toLocaleString()} entr${n === 1 ? 'y' : 'ies'}`;
}

const CampaignSuppressionLists: FunctionComponent<ICampaignSuppressionListsProps> = ({ createdBy, value, onChange }) => {
  const { getSuppressionLists } = useCampaignsRequest();
  const getListsRef = useRef(getSuppressionLists);
  getListsRef.current = getSuppressionLists;

  const [lists, setLists] = useState<ISuppressionList[] | null>(null);
  const [userId, setUserId] = useState<string>();
  const [allAlways, setAllAlways] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Lists are made in Settings, opened from here in a new tab — re-read them on return.
  useEffect(() => {
    let cancelled = false;
    const load = () => getListsRef.current().then(res => {
      if (cancelled || !res?.success) return;
      setLists(res.data.lists);
      setUserId(res.data.userId);
    });
    void load();
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', load);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (e: MouseEvent) => {
      if (!pickerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  const head = (
    <Head>
      <HeadText>
        <Title>Suppression lists</Title>
        <Sub>People on these lists are never added to this campaign, and are taken out if they are already in it.</Sub>
      </HeadText>
      <ManageLink href={AppRoutes.SETTINGS_SUPPRESSION_LIST} target="_blank" rel="noopener noreferrer">
        Manage lists
        <ExternalLink size={12} strokeWidth={2.2} aria-hidden />
      </ManageLink>
    </Head>
  );

  if (!lists) {
    return (
      <Wrap aria-busy="true">
        {head}
        <Group>
          <SkeletonBone $w="56px" $h="10px" />
          <Chips>
            {['112px', '92px', '136px'].map(width => (
              <SkeletonBone key={width} $w={width} $h="28px" $radius="14px" />
            ))}
          </Chips>
        </Group>
        <Group>
          <SkeletonBone $w="150px" $h="10px" />
          <SkeletonBone $h="40px" $radius="8px" />
        </Group>
      </Wrap>
    );
  }

  const always = lists.filter(l => l.scope !== 'selected' && listAppliesTo(l, { createdBy }));
  const optional = lists.filter(l => l.scope === 'selected');
  const picked = new Set(value.map(String));
  const pickedLists = optional.filter(l => picked.has(String(l._id)));
  const shownAlways = allAlways ? always : always.slice(0, ALWAYS_PREVIEW);
  const needle = query.trim().toLowerCase();
  const matching = needle ? optional.filter(l => l.name.toLowerCase().includes(needle)) : optional;

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const openMenu = () => {
    const rect = pickerRef.current?.getBoundingClientRect();
    const below = rect ? window.innerHeight - rect.bottom : MENU_ROOM;
    setDropUp(!!rect && below < MENU_ROOM && rect.top > below);
    setQuery('');
    setOpen(true);
  };

  const onControlClick = (e: ReactMouseEvent) => {
    if (!optional.length || (e.target as HTMLElement).closest('[data-remove]')) return;
    if (open) setOpen(false);
    else openMenu();
  };

  const closeOnEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !open) return;
    e.stopPropagation();
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <Wrap>
      {head}

      {always.length > 0 && (
        <Group>
          <GroupLabel>Always on</GroupLabel>
          <Chips>
            {shownAlways.map(list => (
              <TooltipWrapper key={list._id} tooltipText={`${describeListScope(list, userId)} · ${entries(list.entryCount)}`} placement="top">
                <LockedChip>
                  <Lock size={12} strokeWidth={2.25} aria-hidden />
                  <span>{list.name}</span>
                </LockedChip>
              </TooltipWrapper>
            ))}
            {always.length > ALWAYS_PREVIEW && (
              <MoreChip type="button" onClick={() => setAllAlways(v => !v)}>
                {allAlways ? 'Show less' : `+${always.length - ALWAYS_PREVIEW} more`}
              </MoreChip>
            )}
          </Chips>
        </Group>
      )}

      <Group>
        <GroupLabel id="campaign-suppression-picker">Turned on for this campaign</GroupLabel>
        <Picker ref={pickerRef} onKeyDown={closeOnEscape}>
          <Control $open={open} $disabled={!optional.length} onClick={onControlClick}>
            {pickedLists.map(list => (
              <PickedChip key={list._id}>
                <span>{list.name}</span>
                <RemoveChip
                  type="button"
                  data-remove
                  aria-label={`Turn off ${list.name}`}
                  onClick={() => toggle(String(list._id))}
                >
                  <X size={12} strokeWidth={2.5} />
                </RemoveChip>
              </PickedChip>
            ))}
            <Trigger
              ref={triggerRef}
              type="button"
              disabled={!optional.length}
              aria-haspopup="true"
              aria-expanded={open}
              aria-labelledby="campaign-suppression-picker"
            >
              {!optional.length ? 'No lists to choose yet' : pickedLists.length ? '' : 'Choose lists'}
            </Trigger>
            <Chevron $open={open} aria-hidden><ChevronDown size={16} strokeWidth={2} /></Chevron>
          </Control>

          {open && (
            <Menu $up={dropUp}>
              {optional.length >= SEARCH_FROM && (
                <MenuSearch>
                  <Search size={14} strokeWidth={2} aria-hidden />
                  <input
                    autoFocus
                    value={query}
                    placeholder="Search lists"
                    aria-label="Search lists"
                    onChange={e => setQuery(e.target.value)}
                  />
                </MenuSearch>
              )}
              <MenuList role="group" aria-labelledby="campaign-suppression-picker">
                {matching.map(list => {
                  const on = picked.has(String(list._id));
                  return (
                    <MenuOption key={list._id} $on={on}>
                      <input type="checkbox" checked={on} onChange={() => toggle(String(list._id))} />
                      <CheckBox $on={on} aria-hidden>{on && <Check size={11} strokeWidth={3.5} />}</CheckBox>
                      <OptionText>
                        <OptionName>{list.name}</OptionName>
                        <OptionMeta>
                          {entries(list.entryCount)}
                          {list.owner?.name ? ` · by ${list.owner.name}` : ''}
                        </OptionMeta>
                      </OptionText>
                    </MenuOption>
                  );
                })}
                {!matching.length && <MenuEmpty>No lists match “{query.trim()}”.</MenuEmpty>}
              </MenuList>
              <MenuFoot>
                <span>
                  {pickedLists.length} of {optional.length} on
                  {pickedLists.length > 0 && (
                    <>
                      {' · '}
                      <TextButton type="button" onClick={() => onChange([])}>Clear</TextButton>
                    </>
                  )}
                </span>
                <DoneButton type="button" onClick={() => { setOpen(false); triggerRef.current?.focus(); }}>Done</DoneButton>
              </MenuFoot>
            </Menu>
          )}
        </Picker>
        <Hint>
          {optional.length
            ? 'Turning a list on takes out matching leads already in this campaign when you save.'
            : 'Lists set to “Selected campaigns” show up here. Create one under Manage lists.'}
        </Hint>
      </Group>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const Sub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
`;

const ManageLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; line-height: 20px;
  color: ${styles.colors.primary600};
  text-decoration: none;

  &:hover { color: ${styles.colors.primary600}; text-decoration: underline; }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GroupLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: ${styles.colors.black400};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const LockedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 28px;
  padding: 0 11px 0 10px;
  border: 1px solid #e6eaf2;
  border-radius: 14px;
  background: #f6f7fa;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.black500};
  cursor: default;

  svg { flex-shrink: 0; color: ${styles.colors.black400}; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const MoreChip = styled.button`
  height: 28px;
  padding: 0 11px;
  border: 1px dashed #d0d7e6;
  border-radius: 14px;
  background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.primary600};
  cursor: pointer;

  &:hover { background: ${styles.colors.primary100}; border-color: ${styles.colors.primary200}; }
`;

const Picker = styled.div`
  position: relative;
`;

const Control = styled.div<{ $open: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 40px;
  padding: 5px 36px 5px 6px;
  border: 1.5px solid ${({ $open }) => ($open ? styles.colors.primary600 : '#dde3ee')};
  border-radius: 8px;
  background: ${({ $disabled }) => ($disabled ? '#f8f9fb' : styles.colors.white)};
  box-shadow: ${({ $open }) => ($open ? '0 0 0 3px rgba(89,100,255,0.08)' : 'none')};
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: border-color 0.12s, box-shadow 0.12s;

  ${({ $disabled, $open }) => !$disabled && !$open && `
    &:hover { border-color: ${styles.colors.primary200}; }
    &:focus-within { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
  `}
`;

const Trigger = styled.button`
  flex: 1 1 80px;
  min-width: 80px;
  height: 28px;
  padding: 0 6px;
  border: none;
  background: transparent;
  text-align: left;
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black300};
  cursor: inherit;

  &:focus { outline: none; }
`;

const Chevron = styled.span<{ $open: boolean }>`
  position: absolute;
  top: 50%;
  right: 11px;
  display: inline-flex;
  color: ${styles.colors.black400};
  transform: translateY(-50%) rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s;
  pointer-events: none;
`;

const PickedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  height: 28px;
  padding: 0 4px 0 10px;
  border-radius: 6px;
  background: ${styles.colors.primary100};
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.primary600};
  cursor: default;

  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const RemoveChip = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${styles.colors.primary600};
  cursor: pointer;

  &:hover, &:focus-visible { background: ${styles.colors.primary200}; outline: none; }
`;

const Menu = styled.div<{ $up: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  ${({ $up }) => ($up ? 'bottom: calc(100% + 6px);' : 'top: calc(100% + 6px);')}
  z-index: 30;
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e8f0;
  border-radius: 10px;
  background: ${styles.colors.white};
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06);
  overflow: hidden;
`;

const MenuSearch = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  margin: 0;
  padding: 0 12px;
  border-bottom: 1px solid #eef1f6;
  color: ${styles.colors.black400};

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  }
  input::placeholder { color: ${styles.colors.black300}; }
`;

const MenuList = styled.div`
  max-height: 264px;
  overflow-y: auto;
  padding: 4px;
`;

const MenuOption = styled.label<{ $on: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ $on }) => ($on ? '#f7f8ff' : 'transparent')};
  cursor: pointer;

  &:hover { background: ${({ $on }) => ($on ? '#f0f2ff' : '#f6f7fa')}; }
  &:focus-within { box-shadow: inset 0 0 0 2px ${styles.colors.primary200}; }

  /* Kept for the keyboard and screen readers; CheckBox draws it. */
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
`;

const CheckBox = styled.span<{ $on: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid ${({ $on }) => ($on ? styles.colors.primary600 : '#c6cdda')};
  border-radius: 4px;
  background: ${({ $on }) => ($on ? styles.colors.primary600 : styles.colors.white)};
  color: ${styles.colors.white};
  box-sizing: border-box;
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OptionName = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const OptionMeta = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

const MenuEmpty = styled.div`
  padding: 16px 10px;
  text-align: center;
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black400};
`;

const MenuFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  border-top: 1px solid #eef1f6;
  background: #fafbfd;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

const TextButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.primary600};
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

const DoneButton = styled.button`
  height: 28px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  background: ${styles.colors.primary600};
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.white};
  cursor: pointer;

  &:hover { background: ${styles.colors.primary400}; }
`;

const Hint = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

export default CampaignSuppressionLists;
